import { useState } from 'react';
import { Stats, Configure, useRefinementList } from 'react-instantsearch';
import CurrentRefinements from './CurrentRefinements';
import SortBy from './SortBy';
import Results from './Results';
import { Banners } from './Banner';
import FilterBar from './FilterBar';
import {
  ConfigureProps,
  useConfigure,
  useInstantSearch,
  usePagination,
  useSearchBox,
} from 'react-instantsearch-core';
import getConfig from 'next/config';
import Filter from '@monofront/design-system/visuals/Icons/Old/Filter';
import { useEnrichAlgoliaContexts } from '@hooks/businessContainer';
import { AlgoliaCustomSort, ISearchProduct } from '@monofront/types';
import { useLocale, useTranslations } from 'next-intl';
import CollectionsCarouselTags from './CollectionsCarouselTags';
import HorizontalFilters, {
  SortBy as SortByV2,
} from '@features/HorizontalFilters';
import {
  ALGOLIA_CONTEXT_INSTANT_SEARCH_V2,
  useMerchCard,
} from '@monofront/business/hooks';
import { useCacheFirstAuthentication } from '@context/persistedAuthContext';
import { OneTrustCookieCategories } from '@components/Vendors/Vendors';
import getCookieConsent from '@utils/getCookieConsent';

const {
  publicRuntimeConfig: { algoliaConfig },
} = getConfig();

export type ProductsSearchResultProps = {
  collectionHandle?: string;
  filters?: string;
  hasFilterBar?: boolean;
  setIsCustomSort?: (arg: boolean) => void;
  customSort?: AlgoliaCustomSort<ISearchProduct>;
  hitsPerPage?: number;
  collectionLevel?: number;
  hasCollectionsCarouselTags?: boolean;
  withSortFilterOnly?: boolean;
  withTotalHits?: boolean;
};

const ConfigureAlgolia = (props: ConfigureProps) => {
  const { indexUiState } = useInstantSearch();

  const collection = indexUiState.refinementList?.collections?.[0];

  const ruleContexts = useEnrichAlgoliaContexts(
    collection
      ? ['collection_v2', collection, `collection-${collection}`]
      : [ALGOLIA_CONTEXT_INSTANT_SEARCH_V2],
  );

  const merchCard = useMerchCard(useCacheFirstAuthentication);

  const hitsPerPage = merchCard
    ? (props.hitsPerPage || 40) - 1
    : props.hitsPerPage || 40;

  useConfigure({ ...props, ruleContexts, hitsPerPage });

  return null;
};

/**
 * Enable the `query` widget for Algolia.
 *
 * Every widget must be "activated" for an InstantSearch instance.
 * This means we have to call a widget "connector" to activate it, in our
 * case we don't display a search box in the page results (we display it in the header)
 * this works in CSR, but not in SSR, because we render only this component so Algolia
 * can generate the page's state, thus we connect here, doing nothing and rendering
 * nothing, and this magically let Algolia handle the `query` in URL params.
 *
 * NOTE: by "working in CSR", this means rendering on SSR matching EVERY existing
 * product in the index and correctly filtering on CSR. If you need to check for yourself,
 * log `serverState.initialResults.results` in `pages/search` and check `nbHits`.
 * Should be low (in the hundreds) for a specific request and >4000 if query is not working.
 */
const VirtualSearchBox = () => {
  useSearchBox();

  return null;
};

const VirtualCollectionRefinement = () => {
  useRefinementList({ attribute: 'collections' });

  return null;
};

const VirtualPagination = () => {
  usePagination({ padding: 0 });

  return null;
};

const ProductsSearchResult: React.FC<ProductsSearchResultProps> = ({
  collectionHandle,
  filters,
  hasFilterBar = true,
  setIsCustomSort,
  customSort,
  collectionLevel,
  hasCollectionsCarouselTags = false,
  withSortFilterOnly = false,
  withTotalHits = true,
}) => {
  const locale = useLocale();

  const [isSidebarOpen, toggleSidebarOpen] = useState(false);
  const { customer } = useCacheFirstAuthentication();

  const tCatalog = useTranslations('Catalog');

  const handleToggleSidebar = () => {
    toggleSidebarOpen(!isSidebarOpen);
  };

  return (
    <>
      {hasCollectionsCarouselTags && <CollectionsCarouselTags />}
      <Configure
        userToken={
          getCookieConsent(OneTrustCookieCategories.FUNCTIONALITY)
            ? customer?.uid
            : undefined
        }
      />
      <VirtualSearchBox />
      {withSortFilterOnly && (
        <div className="tw-hidden tw-w-[185px] desktop:tw-block">
          <SortByV2
            setIsCustomSort={setIsCustomSort}
            environment={algoliaConfig.environment}
            isSearch={!collectionHandle}
            locale={locale}
          />
        </div>
      )}
      {hasFilterBar && !withSortFilterOnly && (
        <>
          <HorizontalFilters collectionLevel={collectionLevel}>
            <SortByV2
              setIsCustomSort={setIsCustomSort}
              environment={algoliaConfig.environment}
              isSearch={!collectionHandle}
              locale={locale}
            />
          </HorizontalFilters>
          <FilterBar
            isSidebarOpen={isSidebarOpen}
            handleToggleSidebar={handleToggleSidebar}
            collectionLevel={collectionLevel}
          />
        </>
      )}
      <VirtualPagination />
      {collectionHandle && <VirtualCollectionRefinement />}
      <ConfigureAlgolia clickAnalytics filters={filters} />
      <div className="container-main">
        <div className="container-products tw-mx-3 md:tw-mx-0">
          <div className="header-products">
            {withTotalHits && (
              <span className="mobile-only">
                <span className="nb-hits">
                  <Stats
                    translations={{
                      rootElementText({ nbHits }) {
                        return `${nbHits} `;
                      },
                    }}
                  />
                </span>{' '}
                {tCatalog('results')}
              </span>
            )}
            <div className="filters">
              {hasFilterBar && !withSortFilterOnly && (
                <span onClick={handleToggleSidebar} className="filter-mobile">
                  <Filter />
                  {tCatalog('filter')}
                </span>
              )}
              <div className="sort-by mobile-only tw-my-2">
                <SortBy
                  setIsCustomSort={setIsCustomSort}
                  environment={algoliaConfig.environment}
                  isSearch={!collectionHandle}
                  locale={locale}
                />
              </div>
            </div>
          </div>
          <div
            //TODO: finish container & media queries stuff https://lafourche.atlassian.net/browse/LF-2332 and apply it here
            className={`
                tw-mb-2
                max-[800px]:tw-px-[3.2vw]
              `}
          >
            <CurrentRefinements withTotalHits={withTotalHits} />
            <div className="tw-my-2 tw-flex tw-w-full tw-justify-center">
              <Banners />
            </div>
          </div>
          <Results customSort={customSort} />
        </div>
      </div>
      <style jsx>{`
        .container-main {
          display: flex;
        }

        .container-products {
          width: 100%;
          flex: 4;
        }

        .header-products {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: var(--space-5);
        }

        .header-products > span {
          display: flex;
        }

        .nb-hits {
          font-weight: var(--font-weight-bold);
          margin-right: var(--space-1);
        }

        .filters {
          display: flex;
        }

        .filter-mobile {
          display: none;
        }

        @media (max-width: 800px) {
          .header-products {
            padding: var(--padding-layout-sm);
            margin: var(--space-1) 0;
          }

          .filter-mobile {
            display: flex;
            align-items: center;
            margin-right: var(--space-4);
          }

          .container-products {
            flex: 1;
          }

          .sort-by {
            width: 70px;
            overflow: hidden;
          }
        }
      `}</style>
    </>
  );
};

export const ProductsSearchResultSSR = () => {
  return null;
};

export default ProductsSearchResult;
